import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faClock, faCoins, faDownload, faKey } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as AnalysisIcon } from "./assets/progress-arrow.svg";
import config from "./config";
import MemberSignalMetrics from "./MemberSignalMetrics";
import SignalScoreGauge from "./SignalScoreGauge";
import LiveAnalysisCharts from "./LiveAnalysisCharts";
import AnalysisCountdown, { AnalysisCanceled } from "./AnalysisCountdown";
import TemporaryActivationCode from "./TemporaryActivationCode";
import { APPEARANCE_CHANGE_EVENT, getAppearancePreference, saveAppearancePreference } from "./plansAppearance";
import "./Services.css";
import "./TeamManagement.css";
import "./Signal.css";

const activeStates = ["Pending", "Approved", "Running"];
const deliveryLabels = { Queued: "Your report is queued for email.", Sending: "Submitting your report to the email provider.",
  Accepted: "Your report was accepted by the email provider.", Rejected: "The email provider rejected your report email.",
  Uncertain: "Email delivery could not be confirmed. Check your inbox before retrying.", Expired: "The report email has expired." };
const stateLabels = { Pending: "Waiting for your desktop app to receive your request.", Approved: "Waiting for your desktop app to start.",
  Running: "Five-minute analysis in progress", Denied: "Analysis declined on your device.", Cancelled: "Analysis cancelled.",
  Expired: "Analysis request expired.", Failed: "Analysis could not finish.", NoCredits: "No report credits available." };

export default function Signal({ signal }) {
  const userId = Number(localStorage.getItem("userID"));
  const token = localStorage.getItem("token") || "";
  const name = [localStorage.getItem("userFirstName"), localStorage.getItem("userLastName")].filter(Boolean).join(" ").trim() || "Your signal";
  const pendingKey = `signalAnalysisPending:${userId}`;
  const [theme, setTheme] = useState(getAppearancePreference);
  const [desktop, setDesktop] = useState(null);
  const [connectionChecked, setConnectionChecked] = useState(false);
  // Connection presentation must change in the same render as the telemetry.
  // The remote-analysis heartbeat only determines analysis availability/capabilities.
  const hasTelemetryStatus = Boolean(signal?.SignalOverallStatus);
  const connected = hasTelemetryStatus && signal.SignalOverallStatus !== "Offline";
  const [credits, setCredits] = useState(null);
  const [creditError, setCreditError] = useState("");
  const [creditLoading, setCreditLoading] = useState(true);
  const [resourceError, setResourceError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [request, setRequest] = useState(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [pending, setPending] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(pendingKey));
      return saved?.targetUserId === userId && ["Test", "Report"].includes(saved.mode)
        ? { requestId: saved.requestId, targetUserId: userId, meetingSessionId: saved.meetingSessionId ?? null, mode: saved.mode } : null;
    } catch { return null; }
  });
  const mounted = useRef(false), mutating = useRef(false), version = useRef(0), controllers = useRef(new Set());
  const creditRefresh = useRef(0);
  const deviceRefresh = useRef(0);
  const previousConnection = useRef(connected);
  const api = useCallback(async (path, method = "GET", body) => {
    const controller = new AbortController();
    controllers.current.add(controller);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${config.API_URL.replace(/\/+$/, "")}/api/${path}`, {
        method, signal: controller.signal, cache: "no-store",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      const value = await response.json().catch(() => ({}));
      if (!response.ok) {
        const failure = new Error(response.status === 401 ? "Sign in again to view your analysis." : value.message || "Analysis is temporarily unavailable.");
        failure.status = response.status; throw failure;
      }
      return value;
    } finally { clearTimeout(timeout); controllers.current.delete(controller); }
  }, [token]);
  const refreshCredits = useCallback(async () => {
    const sequence = ++creditRefresh.current;
    setCreditLoading(true);
    try {
      const account = await api("credits/account");
      if (!Number.isFinite(account.availableReports)) throw new Error("Report balance could not be read.");
      if (mounted.current && sequence === creditRefresh.current) { setCredits(account.availableReports); setCreditError(""); }
    } catch (reason) {
      if (mounted.current && sequence === creditRefresh.current) {
        setCredits(null);
        setCreditError(reason.status === 401 ? "Sign in again to load your report credits."
          : "Your report credits could not be loaded. Retry to enable Analysis + Report.");
      }
    } finally { if (mounted.current && sequence === creditRefresh.current) setCreditLoading(false); }
  }, [api]);
  const loadDevices = useCallback(async () => {
    const sequence = ++deviceRefresh.current;
    try {
      const available = await api(`remote-analysis/current-device?targetUserId=${userId}`);
      if (typeof available?.connected !== "boolean") throw new Error("Your desktop connection could not be checked.");
      if (mounted.current && sequence === deviceRefresh.current) {
        setDesktop(available.connected ? available : null);
        setResourceError("");
      }
    } catch (reason) {
      if (mounted.current && sequence === deviceRefresh.current) { setDesktop(null); setResourceError(reason.message || "Your desktop connection could not be checked."); }
    } finally { if (mounted.current && sequence === deviceRefresh.current) setConnectionChecked(true); }
  }, [api, userId]);
  useEffect(() => {
    mounted.current = true;
    const activeControllers = controllers.current;
    let stopped = false;
    const updateTheme = () => setTheme(getAppearancePreference());
    window.addEventListener(APPEARANCE_CHANGE_EVENT, updateTheme); window.addEventListener("storage", updateTheme);
    async function initialize() {
      const initialVersion = version.current;
      await Promise.all([loadDevices(), refreshCredits()]);
      if (stopped) return;
      try {
        const latest = await api(`remote-analysis/latest?targetUserId=${userId}&mode=Any`);
        const value = latest.request ? await api(`remote-analysis/${latest.request.requestId}?includeLive=true`) : null;
        if (!stopped && initialVersion === version.current) setRequest(value);
      } catch { if (!stopped) setError("Your previous analysis could not be checked. Retry before starting another."); return; }
      if (!stopped) setReady(true);
    }
    initialize();
    let refreshing = false;
    const refresh = async () => {
      if (document.hidden || refreshing || mutating.current) return;
      refreshing = true;
      try { await Promise.all([loadDevices(), refreshCredits()]); } finally { refreshing = false; }
    };
    const timer = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      stopped = true; mounted.current = false; clearInterval(timer);
      activeControllers.forEach(controller => controller.abort());
      window.removeEventListener(APPEARANCE_CHANGE_EVENT, updateTheme); window.removeEventListener("storage", updateTheme);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [api, loadDevices, refreshCredits, userId]);
  useEffect(() => {
    if (previousConnection.current === connected) return;
    previousConnection.current = connected;
    if (connected) {
      // Activation just started producing telemetry; do not wait for the 30-second refresh.
      loadDevices();
    } else {
      deviceRefresh.current++;
      setDesktop(null);
    }
  }, [connected, loadDevices]);
  const active = activeStates.includes(request?.state);
  const sending = request?.deliveries?.some(d => ["Queued", "Sending"].includes(d.state));
  const trackedId = request?.requestId;
  useEffect(() => {
    // Open newly discovered/running analyses, but keep their results open on completion.
    if (active) setAnalysisExpanded(true);
  }, [active, trackedId]);
  useEffect(() => { refreshCredits(); }, [refreshCredits, request?.creditState]);
  useEffect(() => {
    if (!trackedId || (!active && !sending)) return;
    let stopped = false, fetching = false, timer;
    async function poll() {
      if (stopped || document.hidden || fetching) return;
      if (mutating.current) { timer = setTimeout(poll, 2000); return; }
      fetching = true;
      const generation = version.current;
      try {
        const value = await api(`remote-analysis/${trackedId}?includeLive=true`);
        if (!stopped && generation === version.current) { setRequest(value); setError(""); }
      } catch (reason) {
        if (!stopped && generation === version.current) {
          if ([401, 403, 404].includes(reason.status)) { setRequest(null); setReady(false); }
          setError("Analysis status could not be refreshed. Retry to check the existing request.");
        }
      } finally {
        fetching = false;
        if (!stopped) timer = setTimeout(poll, active ? 2000 : 10000);
      }
    }
    const visibility = () => { clearTimeout(timer); if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", visibility); poll();
    return () => { stopped = true; clearTimeout(timer); document.removeEventListener("visibilitychange", visibility); };
  }, [api, trackedId, active, sending]);
  async function submit(mode, retry = false) {
    if (mutating.current || (!retry && (active || pending || !ready))) return;
    const operation = retry ? pending : { requestId: crypto.randomUUID().replace(/-/g, ""), targetUserId: userId, meetingSessionId: null, mode };
    if (!operation) return;
    mutating.current = true; version.current++; setBusy(true); setError("");
    try {
      // Persist before sending: a lost response/reload must never create a second paid request.
      sessionStorage.setItem(pendingKey, JSON.stringify(operation)); setPending(operation);
      const value = await api("remote-analysis", "POST", operation);
      if (mounted.current) { setRequest(value); setAnalysisExpanded(true); setPending(null); sessionStorage.removeItem(pendingKey); }
    } catch (reason) {
      if (mounted.current) {
        const rejected = reason.status && reason.status < 500 && reason.status !== 408;
        if (rejected) { setPending(null); sessionStorage.removeItem(pendingKey); }
        setError(rejected ? reason.message : "The request could not be confirmed. Retry the same analysis below.");
      }
    } finally { mutating.current = false; if (mounted.current) { setBusy(false); refreshCredits(); } }
  }
  async function action(path) {
    if (mutating.current) return;
    mutating.current = true; version.current++; setBusy(true); setError("");
    try { const value = await api(`remote-analysis/${trackedId}/${path}`, "POST"); if (mounted.current) setRequest(value); }
    catch (reason) { if (mounted.current) setError(reason.message || "The action could not be confirmed. Retry it."); }
    finally { mutating.current = false; if (mounted.current) { setBusy(false); refreshCredits(); } }
  }
  async function retryStatus() {
    if (mutating.current) return;
    mutating.current = true; version.current++; setBusy(true);
    try {
      const latest = await api(`remote-analysis/latest?targetUserId=${userId}&mode=Any`);
      const value = latest.request ? await api(`remote-analysis/${latest.request.requestId}?includeLive=true`) : null;
      if (mounted.current) { setRequest(value); setReady(true); setError(""); }
      await Promise.all([loadDevices(), refreshCredits()]);
    } catch { if (mounted.current) setError("Analysis status could not be checked. Please retry."); }
    finally { mutating.current = false; if (mounted.current) { setBusy(false); refreshCredits(); } }
  }
  const selected = connected ? desktop : null;
  const blocked = busy || active || !!pending || !ready || !selected;
  const reportBlockedReason = busy ? "Updating your analysis."
    : active ? "Finish or cancel your current analysis before generating another report."
    : pending ? "Confirm the previous request with Retry same analysis before starting another."
    : !ready ? "Checking your previous analysis before starting another."
    : !connected ? "Connect your desktop client to generate a report."
    : !selected ? "Waiting for your desktop client to be ready for analysis."
    : credits === null ? creditLoading ? "Loading your report credits." : creditError
    : credits < 1 ? "You need 1 available credit to generate a report." : "";
  return <main className={`service-monitor-page service-monitor-page--${theme} team-page signal-page`}>
    <div className="service-monitor-inner">
      <header className="service-monitor-header"><div><p className="service-monitor-eyebrow">YOUR SIGNAL</p><h1>Your connection, in focus.</h1>
        <p className="service-monitor-intro">See how your workspace, network and device are performing. Test your connection live or get a detailed report.</p></div>
        <div className="service-monitor-theme" role="group" aria-label="Website appearance">{["light", "dark"].map(value =>
          <button key={value} type="button" aria-pressed={theme === value} onClick={() => saveAppearancePreference(value)}>{value === "light" ? "Light" : "Dark"}</button>)}</div>
      </header>
      <section className="service-monitor-panel signal-overview" aria-label="Your signal">
        <div className="service-monitor-toolbar"><div><h2>My signal</h2><p>Current status from your desktop client</p></div>
        </div>
        <section className="signal-desktop-connection" aria-label="Desktop client connection">
          <div className="signal-desktop-status">
            <h3>Desktop client</h3>
            <p role="status" className={connected ? "signal-desktop-status--connected"
              : hasTelemetryStatus || (connectionChecked && !resourceError) ? "signal-desktop-status--disconnected" : ""}>
              {!hasTelemetryStatus && !connectionChecked ? "Checking desktop connection..."
                : !hasTelemetryStatus && resourceError ? "Desktop connection unavailable"
                : connected ? "Connected" : "Not Connected"}
            </p>
          </div>
          {!connected && (hasTelemetryStatus || (connectionChecked && !resourceError)) && <>
            <p className="signal-muted">Download SignalTuner if you need the app, or generate a code and enter it in your desktop app to activate it. Keep the app open to connect.</p>
            <div className="signal-desktop-setup">
              <Link className="signal-download-button" to="/downloads"><FontAwesomeIcon icon={faDownload} className="signal-setup-icon" aria-hidden="true" />Download SignalTuner</Link>
              <TemporaryActivationCode compact generateIcon={<FontAwesomeIcon icon={faKey} className="signal-setup-icon" aria-hidden="true" />} />
            </div>
          </>}
        </section>
        <div className="service-monitor-row team-member-row signal-self-row"><div className="team-member-row-main">
          <div className="service-monitor-identity"><span className="team-member-avatar" aria-hidden="true">{name.split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase()}</span>
            <div><h3>{name}</h3><p className="team-member-email">{signal?.SignalOverallStatus === "Offline" ? "Desktop client offline" : signal ? "Your current signal" : "Waiting for your desktop client"}</p></div>
          </div>
          <MemberSignalMetrics signal={signal} name={name} renderScore={score => <SignalScoreGauge score={score} />} />
        </div></div>
        {!active && <div className="signal-analysis-options">
          <div className="signal-analysis-actions" role="group" aria-label="Your analysis actions">
            <article className="signal-action-card" aria-labelledby="signal-test-title">
              <span className="signal-action-icon" aria-hidden="true"><AnalysisIcon className="signal-analysis-icon" focusable="false" /></span>
              <div className="signal-action-content">
                <h3 id="signal-test-title">Test Analysis</h3>
                <p id="signal-test-description" className="signal-action-description">Preview how your telemetry will be displayed.</p>
                <p id="signal-test-meta" className="signal-action-meta"><span><FontAwesomeIcon icon={faClock} aria-hidden="true" />5 minutes</span><span><FontAwesomeIcon icon={faCoins} aria-hidden="true" />Free</span></p>
              </div>
              <button type="button" aria-describedby="signal-test-description signal-test-meta" disabled={blocked || !selected?.supportsTestAnalysis} onClick={() => submit("Test")}><AnalysisIcon className="signal-analysis-icon" aria-hidden="true" focusable="false" />Test Analysis</button>
            </article>
            <article className="signal-action-card signal-action-card--report" aria-labelledby="signal-report-title">
              <span className="signal-action-icon" aria-hidden="true"><FontAwesomeIcon icon={faFileLines} /></span>
              <div className="signal-action-content">
                <h3 id="signal-report-title">Analysis + Report</h3>
                <p id="signal-report-description" className="signal-action-description">Run analysis + get a PDF report emailed.</p>
                <p id="signal-report-meta" className="signal-action-meta"><span><FontAwesomeIcon icon={faClock} aria-hidden="true" />5 minutes</span><span><FontAwesomeIcon icon={faCoins} aria-hidden="true" />1 credit</span></p>
              </div>
              <button type="button" className="signal-report-button" aria-describedby={reportBlockedReason ? "signal-report-reason" : "signal-report-description signal-report-meta"} disabled={blocked || credits === null || credits < 1} onClick={() => submit("Report")}><AnalysisIcon className="signal-analysis-icon" aria-hidden="true" focusable="false" />Analysis + Report</button>
            </article>
          </div>
        </div>}
        {!active && <footer className="signal-credit-balance"><span aria-live="polite">Available credits: <strong>{credits ?? "Unavailable"}</strong></span><Link className="signal-add-credits-button" to="/plans"><FontAwesomeIcon icon={faCoins} aria-hidden="true" />Add Credits</Link></footer>}
        {!active && reportBlockedReason && <p id="signal-report-reason" className="signal-notice" role="status">{reportBlockedReason}</p>}
        {!active && creditError && <div className="signal-notice" role="alert">
          {reportBlockedReason !== creditError && <p>{creditError}</p>}
          <button type="button" disabled={creditLoading} onClick={refreshCredits}>Retry credit balance</button>
        </div>}
        {!active && selected && !selected.supportsTestAnalysis && <p className="signal-notice">Update your desktop app to enable Test Analysis. Report generation remains available.</p>}
        {resourceError && <div role="alert" className="signal-notice"><p>Desktop analysis availability could not be checked. {resourceError}</p><button type="button" onClick={loadDevices}>Retry analysis availability</button></div>}
        {error && <div className="signal-notice" role="alert"><p>{error}</p><button type="button" disabled={busy} onClick={retryStatus}>Refresh analysis status</button></div>}
        {pending && <div className="signal-notice"><p>A {pending.mode === "Test" ? "Test Analysis" : "report request"} needs confirmation.</p>
          <button type="button" disabled={busy} onClick={() => submit(pending.mode, true)}>Retry same analysis</button></div>}
        {busy && <p role="status" className="signal-notice">Updating analysis…</p>}
      </section>
      <section className="service-monitor-panel signal-live-panel" aria-label="Your analysis">
        <div className="service-monitor-toolbar signal-live-header"><div><h2>{request?.mode === "Test" ? "Test Analysis" : request ? "Report analysis" : "Live telemetry"}</h2>
          <p className={request?.state === "Running" ? "signal-analysis-running" : undefined}>{request?.state === "Cancelled" ? <AnalysisCanceled /> : <>{request ? request.state === "Completed" ? request.mode === "Test" ? "Test complete. No report generated or credit used." : "Report generated."
            : stateLabels[request.state] || request.state : "Start an analysis to watch your connection for five minutes."}
            {request?.state === "Running" && <AnalysisCountdown key={trackedId} telemetry={request.liveTelemetry} />}</>}</p></div>
          <div className="signal-live-actions">
            {active && <button className="signal-cancel-analysis" type="button" disabled={busy} onClick={() => action("cancel")}>Cancel analysis</button>}
            <button className="signal-analysis-toggle" type="button" aria-label={analysisExpanded ? "Collapse analysis" : "Expand analysis"} aria-expanded={analysisExpanded} aria-controls="signal-analysis-details" onClick={() => setAnalysisExpanded(value => !value)}>
              <span className="signal-analysis-disclosure" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div id="signal-analysis-details" hidden={!analysisExpanded}>
        {request ? <div className="signal-live-content">
          <LiveAnalysisCharts telemetry={request.liveTelemetry} running={request.state === "Running"} />
          {request.mode !== "Test" && <p>{request.creditState === "Consumed" ? "1 credit used. Email retries do not use another credit."
            : request.creditState === "Reserved" ? "1 credit reserved." : request.creditState === "Released" ? "Your reserved credit was returned." : "No report credit used yet."}</p>}
          {request.deliveries?.map(delivery => <p role="status" key={delivery.recipient}>{deliveryLabels[delivery.state] || delivery.state}</p>)}
          {request.deliveries?.some(d => d.state === "Rejected") && <button type="button" disabled={busy} onClick={() => action("retry-delivery")}>Retry report email</button>}
        </div> : <div className="signal-live-empty"><span aria-hidden="true">↗</span><h3>A clearer picture in five minutes.</h3>
          <p>Watch Wi-Fi strength, bandwidth, latency, packet loss, CPU and memory as they change. Test Analysis is free and does not create a report.</p></div>}
        </div>
      </section>
    </div>
  </main>;
}
